import React, { useContext, useState } from "react";
import { useForm } from "react-final-form";
import { IntlContext } from "context";
import { fetchAutocompleteAddress } from "hooks/autocomplete";
import { Autocomplete } from "components/Form/Field";
import { LabelProps } from "ui/Form/Element/Label";
import { FieldControlProps } from "ui/Form/Element/FieldControl";

interface AddressFeature {
    properties: {
        label: string;
        context?: string;
        housenumber?: string;
        street?: string;
        postcode?: string;
        city?: string;
        citycode?: string;
    };
    geometry: {
        coordinates: [number, number];
    };
}

interface Props {
    labelProps?: LabelProps & { label: React.ReactNode };
    inputProps: {
        name: string;
        type?: string;
        latitudeName: string;
        longitudeName: string;
        houseNumber?: string;
        street?: string;
        postcode?: string;
        city?: string;
        commune_code?: string;
        department_code?: string;
        required: boolean;
        placeholder?: string;
    };
    fieldControlProps?: FieldControlProps;
}

const AddressAutocomplete: React.FC<Props> = ({ labelProps, inputProps, fieldControlProps }) => {
    const intl = useContext(IntlContext);
    const { batch, change } = useForm();
    const [addresses, setAddresses] = useState<AddressFeature[]>();

    const clearGeocodedFields = () => {
        batch(() => {
            change(inputProps.longitudeName, null);
            change(inputProps.latitudeName, null);
            inputProps.houseNumber && change(inputProps.houseNumber, undefined);
            inputProps.street && change(inputProps.street, undefined);
            inputProps.postcode && change(inputProps.postcode, undefined);
            inputProps.city && change(inputProps.city, undefined);
            inputProps.commune_code && change(inputProps.commune_code, undefined);
            inputProps.department_code && change(inputProps.department_code, undefined);
        });
    };

    return (
        <Autocomplete
            inputProps={Object.assign({}, inputProps, {
                placeholder: inputProps.placeholder ?? (inputProps.type === "municipality" ? intl.formatMessage("service.placeholder.address_city") : intl.formatMessage("generic.placeholder.address")),
                keySearch: "q",
                valueKeySearch: "q",
                minSearch: 3,
                dataLabel: (address: Record<string, unknown>) => {
                    const a = address as unknown as AddressFeature;
                    return inputProps.type === "municipality"
                        ? `${a.properties.label} - ${a.properties.context ?? ""}`
                        : a.properties.label;
                },
                dataValue: (address: Record<string, unknown>) => (address as unknown as AddressFeature).properties.label,
                dataFetcher: (params: Record<string, unknown>) =>
                    fetchAutocompleteAddress(
                        Object.assign({}, params, {
                            limit: 15,
                        }),
                        inputProps.type
                    ),
                getRows: (res: unknown) => {
                    const typed = res as { data: { features: AddressFeature[] } };
                    setAddresses(typed.data.features);
                    return typed.data.features;
                },
                onSelect: (value: string | number) => {
                    if (!addresses) {
                        return;
                    }

                    const address = addresses.find((item) => item.properties.label === value);

                    if (!address) {
                        return;
                    }

                    batch(() => {
                        change(inputProps.longitudeName, address.geometry.coordinates[0]);
                        change(inputProps.latitudeName, address.geometry.coordinates[1]);
                        inputProps.houseNumber && change(inputProps.houseNumber, address.properties.housenumber);
                        inputProps.street && change(inputProps.street, address.properties.street);
                        inputProps.postcode && change(inputProps.postcode, address.properties.postcode);
                        inputProps.city && change(inputProps.city, address.properties.city);
                        inputProps.commune_code && change(inputProps.commune_code, address.properties.citycode);
                        inputProps.department_code &&
                            change(
                                inputProps.department_code,
                                (address.properties.context ?? "").substring(0, 2)
                            );
                    });
                },
                onInputValueChange: clearGeocodedFields,
                onClearCallBack: clearGeocodedFields,
                noResultsMessage: intl.formatMessage("generic.label.no_results"),
            })}
            labelProps={labelProps}
            fieldControlProps={fieldControlProps}
        />
    );
};

export default AddressAutocomplete;
